'use client';
import Image from '@/components/Image';
import { useIrclickidCheck } from '@/hooks/useIrclickidCheck';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { addSearchParams } from '@/utils/tools';
import { CloseOutlined, SearchOutlined, WapMoreOutlined } from '@zeelool/icon';
import Link from 'next/link';
import NextImage from 'next/image';
import { useTranslations } from '@/i18n/use-translations';
import { useRouter } from 'next/navigation';
import { Suspense, useEffect, useRef, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { HeaderProps } from '.';
import wapCartIcon from './assets/cart-icon.svg';
import AccountIcon from './components/AccountIcon';
import CartIcon from './components/CartIcon';
import DingTong from './components/DingTong';
import SearchPopup from './components/SearchComponent/SearchPopup.m';
import LeftSideNavWrapper from './components/SideNavWrapper.m';
import type { NavigationPage } from './components/wapNavigation/navigation.types';
import { shouldShowTermsUpdate } from './terms-update';

export type MobileHeaderDisplayMode = 'full' | 'ding-tong-only' | 'hidden';

export function getMobileHeaderDisplayMode(
  pathname: string,
  isShowHeader: boolean,
): MobileHeaderDisplayMode {
  if (pathname === '/cart' || pathname.startsWith('/cart/')) {
    return 'hidden';
  }

  return isShowHeader ? 'full' : 'hidden';
}

const Header = (
  props: HeaderProps & {
    cartProductNum: number | string;
    deviceType: EPlatform;
    isShowHeader: boolean;
    isDarkMode: boolean;
    isShowDtBannerByPage: boolean;
    isShowAppDownloadBanner: boolean;
    loadSearchDataIfNeeded: () => Promise<void>;
    isBot?: boolean;
  },
) => {
  const {
    deviceType,
    isShowHeader,
    isDarkMode,
    isShowDtBannerByPage,
    isShowAppDownloadBanner,
    loadSearchDataIfNeeded,
  } = props;
  // 使用路由元数据Hook
  const { cnName, pathname } = useRouteMetadata();
  const [downloadVisible, setDownloadVisible] = useState(true);
  const [showSearchPopup, setShowSearchPopup] = useState(false);
  const [navigationPage, setNavigationPage] = useState<NavigationPage>('primary');
  const globalHeaderRef = useRef<HTMLDivElement>(null);
  const setIsOpenSideNav = useStore((state) => state.setIsOpenSideNav);
  const isOpenSideNav = useStore((state) => state.isOpenSideNav);
  const serverTimestamp = useStore((state) => state.publicResourceData?.info?.timestamp);
  const showTermsUpdate = shouldShowTermsUpdate(serverTimestamp);
  const router = useRouter();
  const tA11y = useTranslations('layout.header.a11y');
  const tDialog = useTranslations('dialog');
  const tSiteConfig = useTranslations('layout.siteConfig');
  const tFloat = useTranslations('layout.rightBottomFloating');
  const displayMode = getMobileHeaderDisplayMode(pathname, isShowHeader);
  const isDingTongOnly = displayMode === 'ding-tong-only';

  const { trackEvent } = useReporting();
  const timer = useRef<NodeJS.Timeout | null>(null);

  // 使用 irclickid 检查 Hook
  const { isIrclickid, getWebUrlWithIrclickid } = useIrclickidCheck();

  useEffect(() => {
    return () => {
      if (timer.current) {
        clearInterval(timer.current);
      }
    };
  }, []);

  const handleAppDownload = () => {
    trackEvent(
      {
        eventName: 'DownloadAppBar_Click',
        properties: {
          page_name: cnName,
          url_path: window.location.href,
        },
      },
      [ProviderType.SENSORS],
    );

    // 处理web_url，如果当前URL没有irclickid，尝试添加
    const webUrlWithIrclickid = getWebUrlWithIrclickid(window.location.href);

    window.location.href = addSearchParams('https://zeelool.onelink.me/QxSq/k76k1dbw', {
      pub_zl_utm_source:
        window.latestChannelAttribute?.expires > Date.now()
          ? window.latestChannelAttribute?.pub_zl_utm_source || undefined
          : undefined,
      pub_zl_utm_medium:
        window.latestChannelAttribute?.expires > Date.now()
          ? window.latestChannelAttribute?.pub_zl_utm_medium || undefined
          : undefined,
      pub_zl_utm_campaign:
        window.latestChannelAttribute?.expires > Date.now()
          ? window.latestChannelAttribute?.pub_zl_utm_campaign || undefined
          : undefined,
      pub_zl_utm_campaignid:
        window.latestChannelAttribute?.expires > Date.now()
          ? window.latestChannelAttribute?.pub_zl_utm_campaignid || undefined
          : undefined,
      pub_zl_referrer:
        window.latestChannelAttribute?.expires > Date.now()
          ? window.latestChannelAttribute?.pub_zl_referrer || undefined
          : undefined,
      pub_zl_page:
        window.latestChannelAttribute.expires > Date.now() ||
        window.latestChannelAttribute.expires === -1
          ? window.latestChannelAttribute.pub_zl_page || undefined
          : undefined,
      web_url: webUrlWithIrclickid,
    });
  };

  // 图标颜色类名
  const effectiveDarkMode = isDarkMode && !isOpenSideNav;
  const iconColorClass = effectiveDarkMode ? 'text-white' : 'text-primary-black';

  const handleSideNavTriggerClick = () => {
    if (!isOpenSideNav) {
      setNavigationPage('primary');
      setIsOpenSideNav(true);
      trackEvent(
        {
          eventName: 'TopNavTab_Click',
          properties: {
            tab_name: '左侧菜单按钮',
            page_name: cnName,
            url_path: window.location.href,
            tab_index: 0,
          },
        },
        [ProviderType.SENSORS],
      );
      return;
    }

    if (navigationPage === 'secondary') {
      setNavigationPage('primary');
      return;
    }

    setNavigationPage('primary');
    setIsOpenSideNav(false);
  };

  const SDSearchEntryBtn_Click = () => {
    trackEvent(
      {
        eventName: 'SearchEntryBtn_Click',
        properties: {
          page_name: cnName,
          url_path: window.location.href,
        },
      },
      [ProviderType.SENSORS],
    );
    trackEvent(
      {
        eventName: 'TopNavTab_Click',
        properties: {
          tab_name: '搜索',
          page_name: cnName,
          url_path: window.location.href,
          tab_index: 1,
        },
      },
      [ProviderType.SENSORS],
    );
  };

  return (
    <div
      id="header"
      ref={globalHeaderRef}
      className={`HeaderSection sticky top-0 z-[400] w-full box-border ${
        displayMode === 'hidden' ? 'hidden' : ''
      } ${
        isDingTongOnly
          ? 'bg-transparent shadow-none'
          : effectiveDarkMode
            ? 'bg-[rgba(0,0,0,0.7)] shadow-none'
            : 'bg-primary-white shadow-none'
      }`}
    >
      {!isDingTongOnly && (
        <Link href="/accessibility-statement" className="sr-only">
          Accessibility Statement
        </Link>
      )}
      {/* APP下载提示信息 */}
      {!isDingTongOnly && env.NEXT_PUBLIC_HAS_APP === 'true' && (
        <InView
          as="div"
          triggerOnce
          onChange={(inView) => {
            if (inView) {
              trackEvent(
                {
                  eventName: 'DownloadAppBar_Exp',
                  properties: {
                    page_name: cnName,
                    url_path: window.location.href,
                  },
                },
                [ProviderType.SENSORS],
              );
            }
          }}
          className={`flex items-center p-[0_3.733vw] w-full transition-all duration-300 bg-[#000000] ${
            isIrclickid
              ? "bg-[url('/zeelool-next/home-page/top-app-download2.png')]"
              : "bg-[url('/zeelool-next/home-page/top-app-download.png')]"
          } bg-no-repeat bg-[position:top_left] bg-[size:100%_100%] ${
            downloadVisible && isShowAppDownloadBanner ? 'h-[17.1vw]' : 'h-0 overflow-hidden'
          }`}
        >
          <button
            type="button"
            className="ml-[-1.600vw] flex cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-primary-white"
            onClick={() => setDownloadVisible(false)}
            aria-label={tA11y('dismissAppDownloadBanner')}
          >
            <CloseOutlined className="text-[7.467vw]" aria-hidden />
          </button>
          <button
            id={deviceType === EPlatform.IOS ? 'header-app-ios' : 'header-app-android'}
            className="ml-auto flex items-center justify-center w-[51px] h-[26px] text-white bg-transparent rounded-[13px] font-bold p-0 border-none text-xs"
            aria-label="Download APP"
            onClick={handleAppDownload}
          />
        </InView>
      )}
      {/* 顶通信息 */}
      {!!props.dtInfo && isShowDtBannerByPage && <DingTong {...props} />}
      {/* 导航栏信息 */}
      <div
        className={`${isDingTongOnly ? 'hidden' : ''} relative flex h-[48px] w-full items-center justify-between px-[12px] ${
          effectiveDarkMode ? 'bg-[rgba(0,0,0,0.7)] shadow-none' : 'bg-primary-white'
        }`}
        // 防止 iOS 滚动时 Header 图标上下抖动
        style={{
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          perspective: '1000px',
        }}
      >
        <ul
          className="flex shrink-0 items-center gap-[16px]"
          style={{ transform: 'translateZ(0)' }}
        >
          <li className="flex size-[24px] shrink-0 items-center justify-center">
            <button
              type="button"
              aria-label={
                !isOpenSideNav
                  ? tA11y('openMenu')
                  : navigationPage === 'secondary'
                    ? tSiteConfig('backAriaLabel')
                    : tDialog('closeAriaLabel')
              }
              aria-expanded={isOpenSideNav}
              aria-controls="mobile-side-nav-panel"
              className="relative p-0 m-0 border-0 bg-transparent flex items-center justify-center h-full w-full cursor-pointer"
              onClick={handleSideNavTriggerClick}
            >
              {!isOpenSideNav ? (
                <>
                  <WapMoreOutlined className={`text-[24px] ${iconColorClass}`} aria-hidden />
                  {showTermsUpdate ? (
                    <span
                      className="absolute right-0 top-0 size-[8px] rounded-full bg-primary-purple"
                      aria-hidden
                    />
                  ) : null}
                </>
              ) : (
                <NextImage
                  src={
                    navigationPage === 'secondary'
                      ? '/zeelool-next/wap-navigation/header/back.svg'
                      : '/zeelool-next/wap-navigation/header/close.svg'
                  }
                  width={24}
                  height={24}
                  alt=""
                  aria-hidden
                  unoptimized
                />
              )}
            </button>
          </li>
          <li className="flex size-[24px] shrink-0 items-center justify-center">
            <button
              type="button"
              aria-label={tA11y('openSearch')}
              className="p-0 m-0 border-0 bg-transparent flex items-center justify-center h-full w-full cursor-pointer"
              onClick={async () => {
                loadSearchDataIfNeeded();
                setShowSearchPopup(true);
                SDSearchEntryBtn_Click();
              }}
            >
              <SearchOutlined
                className={`size-[24px] shrink-0 text-[24px] ${iconColorClass}`}
                aria-hidden
              />
            </button>
          </li>
        </ul>
        <Link
          prefetch={false}
          aria-label="ZEELOOL"
          href="/"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (window.location.pathname === '/') {
              return;
            }
            trackEvent(
              {
                eventName: 'TopNavTab_Click',
                properties: {
                  tab_name: 'Logo',
                  page_name: cnName,
                  url_path: window.location.href,
                  tab_index: 2,
                },
              },
              [ProviderType.SENSORS],
            );
            router.push('/');
          }}
          className="absolute left-1/2 top-1/2 flex h-[40px] w-[130px] -translate-x-1/2 -translate-y-1/2 items-center justify-center"
        >
          {props.logoData?.logo_image && (
            <Image
              src={effectiveDarkMode ? '/white-logo.png' : props.logoData.logo_image}
              alt="ZEELOOL"
              fill
              fetchPriority="high"
              priority
            />
          )}
          {props.logoData?.logo_sub_image && (
            <Image src={props.logoData.logo_sub_image} alt="ZEELOOL" fill />
          )}
        </Link>

        <ul
          className="flex h-full shrink-0 items-center gap-[16px] [&>li]:!mr-0"
          style={{ transform: 'translateZ(0)' }}
        >
          <AccountIcon isDarkMode={effectiveDarkMode} deviceType={deviceType} />

          <CartIcon
            isDarkMode={effectiveDarkMode}
            cartProductNum={props.cartProductNum}
            deviceType={deviceType}
            icon={
              <NextImage
                src={wapCartIcon}
                alt=""
                width={24}
                height={24}
                unoptimized
                aria-hidden
                className={`size-[24px] shrink-0 ${effectiveDarkMode ? 'brightness-0 invert' : ''}`}
              />
            }
          />
        </ul>
      </div>
      {!isDingTongOnly && (
        <LeftSideNavWrapper
          navData={props.headerNavList}
          isBot={props.isBot ?? false}
          page={navigationPage}
          onPageChange={setNavigationPage}
        />
      )}
      {!isDingTongOnly && (
        <Suspense fallback={null}>
          <SearchPopup show={showSearchPopup} onClose={() => setShowSearchPopup(false)} />
        </Suspense>
      )}
    </div>
  );
};

export default Header;
